// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import BusComponent from '../BusComponent';
import Trigger from 'rc-trigger';
import { focusFirstChild } from '../../helpers/Util';
import AreaSelectorPopup from './AreaSelectorPopup';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';

const popupAlignOptions = {
    points: ['tl', 'bl'],
    offset: [0, 10],
};

/**
 * @typedef Props
 * @property {import('../../objects/LocationAnalysisItem').default} locationAnalysisItem
 * @property {string} mapInstanceId
 * @property {import('react-intl').intlShape} intl
 *
 * @typedef State
 * @property {import('../../objects/LocationAnalysisItem').default} locationAnalysisItem
 * @property {boolean} popupVisible
 *
 * @extends {BusComponent<Props, State>}
 */
class AreaSelector extends BusComponent {
    constructor(props, context) {
        super(props, context);
        /** @type {State} */
        this.state = {
            locationAnalysisItem: this.props.locationAnalysisItem,
            popupVisible: false,
        };
        this.boundAfterPopupRender = this.afterPopupRender.bind(this);
    }

    componentDidMount() {
        this.bindGluBusEvents({
            LOCATION_ANALYSIS_ITEM_UPDATED: this.onLocationAnalysisItemUpdated,
            OPEN_AREA_SELECTOR_REQUEST: this.handlePopupVisibleChange,
        });
        this.emit('HELP_TOUR_STEP_LOADED');
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    /**
     * @param {object} payload
     * @param {import('../../objects/LocationAnalysisItem').default} payload.locationAnalysisItem
     */
    onLocationAnalysisItemUpdated = ({ locationAnalysisItem }) => {
        this.setState({
            locationAnalysisItem,
        });
    };

    afterPopupRender = visible => {
        if (visible) {
            focusFirstChild(this.popupContainer);
        }
    };

    handlePopupVisibleChange = () => {
        this.setState({ popupVisible: true });
    };

    closePopup = () => {
        this.setState({ popupVisible: false });
    };

    render() {
        const { analysisType, selection } = this.state.locationAnalysisItem;

        const selectionArray = Array.from(selection);
        const unit = analysisType.MAPBOX_ROUTING_PROFILE ? (
            <FormattedMessage id="min" />
        ) : (
            <FormattedMessage
                id="mi"
                values={{
                    count: selectionArray.length > 1 ? selectionArray.length : selectionArray[0],
                }}
            />
        );
        const distance = selectionArray.sort((a, b) => a - b).join(', ');

        return (
            <Trigger
                action={['click']}
                popupVisible={this.state.popupVisible}
                onPopupVisibleChange={this.handlePopupVisibleChange}
                onPopupAlign={this.boundAfterPopupRender}
                popup={
                    <div
                        className="menu-popup"
                        ref={e => {
                            this.popupContainer = e;
                        }}
                    >
                        <AreaSelectorPopup
                            analysisType={analysisType}
                            mapInstanceId={this.props.mapInstanceId}
                            onClose={this.closePopup}
                        />
                    </div>
                }
                popupAlign={popupAlignOptions}
            >
                <div data-tourId={HelpTourTargets.INFO_PANEL_RADIUS_SELECTOR}>
                    <button className="btn-chip" aria-haspopup>
                        <i className="material-icons btn-chip__icon">{analysisType.ICON}</i>
                        <span>{distance}</span>
                        {unit}
                    </button>
                </div>
            </Trigger>
        );
    }
}

AreaSelector.propTypes = {
    locationAnalysisItem: PropTypes.object.isRequired,
    mapInstanceId: PropTypes.string.isRequired,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(AreaSelector);
